import trim from 'lodash.trim';

import get from 'lodash.get';
import pick from 'lodash.pick';
import findIndex from 'lodash.findindex';

import Vue from 'vue';
import uniqueId from 'lodash.uniqueid';

import SetupApi from '@/store/api/setup';
import FormApi from '@/store/api/form';

import CategoryPost from '@/views/CategoryPost';

import { default as router, resetRouter } from '@/router';

import { removeKeys } from '@/helpers/util';

// initial state
// shape: [{ id, quantity }]
const state = {
    loaded: false,
    ready: false,
    error: false,
    CMS: {},
    // config: {},
    messages: [],
    ui: {
        sidebar: false, // sidebar visibility
    },
    uploadEndpoint: trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_UPLOAD_ROUTE}`, '/'),
};

// getters
const getters = {
    // auth: state => state.auth,
    loaded: (state) => state.loaded,
    ready: (state) => state.ready,
    error: (state) => state.error,
    CMS: (state) => state.CMS,
    config: (state) => state.CMS.config,
    menus: (state) => state.CMS.menus,
    messages: (state) => state.messages,
    sidebar: (state) => state.ui.sidebar,

    logo: (state) => {
        let logo = {};
        if (state.CMS.images && state.CMS.images.length > 0) {
            logo = state.CMS.images.find((image) => image.type === 'cms-logo');
        }
        return logo;
    },
    googleRecaptchaSiteKey: (state) => {
        const apiKeys = _.get(state.CMS, 'apiKeys') || [];
        const apiKey = apiKeys.find((apiKey) => apiKey.key === 'google-recaptcha-v3');

        let googleRecaptchaSiteKey;
        if (apiKey) googleRecaptchaSiteKey = apiKey.public;
        return googleRecaptchaSiteKey;
    },
};

// actions
const actions = {
    fetchSetup({ commit }, { base_url }) {
        return SetupApi.fetchSetup(base_url)
            .then((setup) => {
                if (setup) {
                    commit('addSetupToState', setup);
                    commit('mapRouter');
                } else {
                    // throw new Error('There is no content for this domain.')
                    commit('setError', { code: 404, message: 'Not Found!' });
                }
            })
            .catch((error) => commit('fetchFailed', error));
    },
    // pageReady({ commit, state }) {
    //     commit('isReady', true)
    // },
    // pageNotReady({ commit, state }){
    //     commit('isReady', false)
    // },
    sendFormToApi({ commit }, payload) {
        const success_message = {
            id: uniqueId(),
            title: 'Erfolgreich',
            text: 'Ihre Nachricht wurde erfolgreich versendet.',
            type: 'success',
            autoclose: 5000,
        };
        const error_message = {
            id: uniqueId(),
            title: 'Fehler',
            text: 'Ihre Nachricht konnte nicht versendet werden.',
            type: 'danger',
            autoclose: 5000,
        };
        removeKeys(payload, ['__typename']);
        return FormApi.create(payload)
            .then((data) => commit('addMessage', success_message))
            .catch((error) => commit('addMessage', error_message));
    },
};

// mutations
const mutations = {
    addSetupToState(state, cms) {
        state.loaded = true;
        // state.meta = data.setup.meta
        state.CMS = cms;
        // state.config = data.config
    },
    isReady(state, value) {
        if (value === true) state.loaded = true;

        state.ready = value;
    },
    updateSidebarVisibility(state, value) {
        state.ui.sidebar = value;
    },
    mapRouter(state) {
        let loadingRoutes = [];

        const staticRoutes = [
            {
                path: '/news/:key',
                redirect: '/aktuelles/:key',
            },
            {
                path: '/aktuelles/:key',
                name: 'Aktuelles',
                component: CategoryPost,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Aktuelles`,
                    category: {
                        key: 'news',
                    },
                },
            },
            {
                path: '/funktionen/:key',
                redirect: '/funktionen/:key',
            },
            {
                path: '/funktionen/:key',
                name: 'Funktionen',
                component: CategoryPost,
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Funktionen`,
                    category: {
                        key: 'funktionen',
                    },
                },
            },
            {
                path: '*',
                name: 'Page',
                component: () => import('@/views/PageNotFound.vue'),
                meta: {
                    icon: null,
                    title: `${state.CMS.name} - Seite nicht gefunden`,
                    skipMiddleware: true,
                },
            },
            // add static category routes
        ];

        if (state.CMS.menus && state.CMS.menus.length) {
            const nodeRoutes = (data) => {
                let routes = [],
                    items = data.items;
                items.forEach((item, index) => {
                    if (item.disabled) return true;

                    let children = null;
                    if (item.children) {
                        children = nodeRoutes({ items: item.children });
                    }

                    let component, view;
                    if (item.page && item.page.view) {
                        component = () => import(`@/views/${item.page.view}.vue`);
                        view = item.page.view;
                    }

                    switch (view) {
                        case 'Redirect':
                            const type = get(item.page, 'hyperlink.type');
                            const redirect = get(item.page, 'hyperlink.menuItem.path');
                            if (type === 'internal' && redirect) {
                                const node = {
                                    name: item.name || '',
                                    path: item.path,
                                    redirect,
                                    meta: {
                                        rootIndex: index,
                                        icon: item.icon,
                                        title: `${state.CMS.name} - ${item.title}`,
                                    },
                                };
                                routes.push(node);
                            }
                            break;

                        default:
                            const node = {
                                name: item.name || '',
                                path: item.path,
                                component,
                                meta: {
                                    rootIndex: index,
                                    icon: item.icon,
                                    title: `${state.CMS.name} - ${item.title}`,
                                },
                                children: children,
                            };
                            routes.push(node);
                    }
                });
                return routes;
            };

            let dynamicRoutes = [];
            state.CMS.menus.forEach((menu) => {
                nodeRoutes({ items: menu.menuItems }).forEach((item) => dynamicRoutes.push(item));
            });

            dynamicRoutes.forEach((route) => {
                loadingRoutes.push(route);
            });
        }

        staticRoutes.forEach((route) => {
            loadingRoutes.push(route);
        });

        resetRouter();
        router.addRoutes(loadingRoutes);
    },
    fetchFailed(state, error) {
        /* eslint no-console: ["error", { allow: ["error"] }] */
        console.error(error);
    },
    setError(state, error) {
        state.error = error;
    },
    addMessage(state, message) {
        const messages = get(state, 'messages', []);
        const i = messages.length;

        const search = pick(message, ['title', 'text', 'info', 'type']);
        const existing = findIndex(messages, search);
        if (existing >= 0) {
            Vue.set(state.messages[existing], 'autoclose', get(message, 'autoclose'));
            Vue.set(state.messages[existing], 'highlight', true);
            setTimeout(() => {
                if (get(state.messages[existing], 'highlight')) Vue.set(state.messages[existing], 'highlight', false);
            }, 200);
        } else {
            Vue.set(state.messages, i, message);
        }
    },

    removeMessage(state, id) {
        const message = state.messages.find((message) => message.id === id);
        const i = findIndex(state.messages, message);

        if (i !== -1) {
            Vue.delete(state.messages, i);
        }
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
